import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { resolveAdminMediaUrl } from '@formosa/shared/utils/mediaUrl';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import CustomerCenterTypeEditModal from './CustomerCenterTypeEditModal';
import {
  CST_STATE,
  CST_STATE_FILTER_OPTIONS,
  CST_TYPE_FILTER_OPTIONS,
  buildCustomerServiceTypePageParams,
  formatCustomerServiceTypeState,
  formatCustomerServiceTypeType,
} from './customerCenterTypeConstants';

const EMPTY_FILTERS = {
  state: '',
  type: '',
  name: '',
};

const TABLE_COLUMNS = ['客服種類（英語）', '客服種類圖標', '客服種類類型', '狀態', '順序', '操作'];

export default function CustomerCenterTypePage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [disableTarget, setDisableTarget] = useState(null);
  const [enableTarget, setEnableTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.customerServiceTypePageQuery(
        buildCustomerServiceTypePageParams(pageNo, nextFilters)
      );
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total ?? 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    setMessage('');
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    setMessage('');
    loadRecords(1, reset);
  }

  function openCreate() {
    setEditId(null);
    setEditOpen(true);
    setMessage('');
  }

  function openEdit(row) {
    setEditId(row.id);
    setEditOpen(true);
    setMessage('');
  }

  async function confirmDelete() {
    if (!deleteTarget?.id) {
      return;
    }
    setActionLoading(true);
    setError('');
    try {
      await adminApi.deleteCustomerServiceType(deleteTarget.id);
      setMessage('刪除成功');
      setDeleteTarget(null);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '刪除失敗');
    } finally {
      setActionLoading(false);
    }
  }

  async function confirmDisable() {
    if (!disableTarget?.id) {
      return;
    }
    setActionLoading(true);
    setError('');
    try {
      await adminApi.updateCustomerServiceTypeState({ id: disableTarget.id, state: CST_STATE.OFF });
      setMessage('已禁用');
      setDisableTarget(null);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '操作失敗');
    } finally {
      setActionLoading(false);
    }
  }

  async function confirmEnable() {
    if (!enableTarget?.id) {
      return;
    }
    setActionLoading(true);
    setError('');
    try {
      await adminApi.updateCustomerServiceTypeState({ id: enableTarget.id, state: CST_STATE.ON });
      setMessage('已啟用');
      setEnableTarget(null);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '操作失敗');
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>客服種類管理</h2>
        <div className="section-header__actions">
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            新增
          </button>
        </div>
      </div>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="cstFilterState">客服狀態</label>
          <select
            id="cstFilterState"
            value={filters.state}
            onChange={(e) => updateFilter('state', e.target.value)}
          >
            {CST_STATE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.label} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="cstFilterType">客服種類類型</label>
          <select
            id="cstFilterType"
            value={filters.type}
            onChange={(e) => updateFilter('type', e.target.value)}
          >
            {CST_TYPE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.label} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="cstFilterName">客服種類名稱</label>
          <input
            id="cstFilterName"
            type="text"
            value={filters.name}
            onChange={(e) => updateFilter('name', e.target.value)}
            placeholder="請輸入客服種類名稱"
          />
        </div>
        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜尋
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
        </div>
      </form>

      {message ? <p className="form-message form-message--success">{message}</p> : null}
      {error ? <p className="form-message form-message--error">{error}</p> : null}

      <div className="table-meta">共 {total} 筆</div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  載入中…
                </td>
              </tr>
            ) : records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  無資料
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.id}>
                  <td className="cell-wrap">{row.enName || '未設定'}</td>
                  <td>
                    {row.image ? (
                      <img src={resolveAdminMediaUrl(row.image)} alt="" className="carousel-list-thumb" />
                    ) : (
                      '未設定'
                    )}
                  </td>
                  <td>{formatCustomerServiceTypeType(row.type)}</td>
                  <td>
                    <span
                      className={
                        row.state === CST_STATE.ON
                          ? 'status-text status-text--success'
                          : 'status-text status-text--danger'
                      }
                    >
                      {formatCustomerServiceTypeState(row.state)}
                    </span>
                  </td>
                  <td>{row.sort ?? '—'}</td>
                  <td>
                    <div className="table-actions table-actions--news">
                      <div className="table-actions__row">
                        <button type="button" className="btn btn-success btn-sm" onClick={() => openEdit(row)}>
                          修改
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger-outline btn-sm"
                          onClick={() => setDeleteTarget(row)}
                        >
                          刪除
                        </button>
                      </div>
                      <div className="table-actions__row">
                        {row.state === CST_STATE.ON ? (
                          <button
                            type="button"
                            className="btn btn-outline-neutral btn-sm btn-with-icon"
                            onClick={() => setDisableTarget(row)}
                          >
                            <span className="btn-state-icon btn-state-icon--danger" aria-hidden="true">
                              ✕
                            </span>
                            禁用
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-outline-neutral btn-sm btn-with-icon"
                            onClick={() => setEnableTarget(row)}
                          >
                            <span className="btn-state-icon btn-state-icon--success" aria-hidden="true">
                              ✓
                            </span>
                            啟用
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(next) => loadRecords(next, filters)} />

      <CustomerCenterTypeEditModal
        open={editOpen}
        typeId={editId}
        onClose={() => setEditOpen(false)}
        onSaved={(wasEdit) => {
          setMessage(wasEdit ? '修改成功' : '新增成功');
          loadRecords(page, filters);
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="刪除客服種類"
        message="確定刪除此客服種類？"
        confirmLabel={actionLoading ? '刪除中…' : '刪除'}
        confirmDisabled={actionLoading}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(disableTarget)}
        title="禁用客服種類"
        message="確定禁用此客服種類？"
        confirmLabel={actionLoading ? '處理中…' : '禁用'}
        confirmDisabled={actionLoading}
        onConfirm={confirmDisable}
        onCancel={() => setDisableTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(enableTarget)}
        title="啟用客服種類"
        message="確定啟用此客服種類？"
        confirmLabel={actionLoading ? '處理中…' : '啟用'}
        confirmDisabled={actionLoading}
        onConfirm={confirmEnable}
        onCancel={() => setEnableTarget(null)}
      />
    </div>
  );
}
